import { readonly, ref } from 'vue';

// Staging resolves feature flags in the browser instead of asking PostHog, so QA always sees
// upcoming features without PostHog credentials, network access or a flag-loading delay. This
// is opted into explicitly by the build (VITE_FEATURE_FLAG_SOURCE=local); a missing PostHog key
// or a failed PostHog request never switches it on, so production keeps its controlled rollouts.
export const usesLocalFeatureFlags = (): boolean => import.meta.env.VITE_FEATURE_FLAG_SOURCE === 'local';

export const localFeatureFlagOverridesKey = 'conveyr.featureFlagOverrides';

const isBooleanEntry = (entry: [string, unknown]): entry is [string, boolean] => typeof entry[1] === 'boolean';

const readStoredOverrides = (): Record<string, boolean> => {
    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(localFeatureFlagOverridesKey) ?? '{}');
        if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return {};

        return Object.fromEntries(Object.entries(stored).filter(isBooleanEntry));
    } catch {
        return {};
    }
};

const overrides = ref<Record<string, boolean>>(readStoredOverrides());

// Saved first and only then applied, so a page never shows overrides that a reload would lose.
const storeOverrides = (next: Record<string, boolean>): boolean => {
    try {
        if (Object.keys(next).length === 0) {
            window.localStorage.removeItem(localFeatureFlagOverridesKey);
        } else {
            window.localStorage.setItem(localFeatureFlagOverridesKey, JSON.stringify(next));
        }
    } catch {
        // Storage can be blocked (private windows, site data settings) or full.
        return false;
    }

    overrides.value = next;

    return true;
};

export const localFeatureFlagOverrides = readonly(overrides);

// A flag is on unless a tester has explicitly switched it off. Callers can only ask about
// flags the app defines, so a stored override for a retired flag has no effect.
export const isLocalFeatureFlagEnabled = (flag: string): boolean => overrides.value[flag] ?? true;

// Returns whether the overrides were saved; when they weren't, nothing changes.
export const replaceLocalFeatureFlagOverrides = (next: Record<string, boolean>): boolean => storeOverrides({ ...next });
